import React, { FC, PropsWithChildren, useEffect, useState } from 'react';
import { useConnection, useUser } from '@sezenta/connection';
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd';

// CR004 web tenancy helpers.

export interface Contract {
  businessId: string;
  name?: string;
  contractType?: number;
  status?: boolean;
  // CR004: per-platform access (from GET /auth/contracts?platform=). On web,
  // webAccess=false means the user is only e.g. a driver there — shown but disabled.
  webAccess?: boolean;
  mobileAccess?: boolean;
}

// Persist the last-used contract so a bare `/portal` (and AuthCheck) can route the
// user straight back into where they were.
const LAST_CONTRACT_KEY = 'activeContractId';

export const getLastContractId = (): string | undefined =>
  localStorage.getItem(LAST_CONTRACT_KEY) ?? undefined;

export const setLastContractId = (id: string): void =>
  localStorage.setItem(LAST_CONTRACT_KEY, id);

// Legacy/default contract id. New tenants use UUID v7; '1' is grandfathered.
export const DEFAULT_CONTRACT_ID = '1';

// Contract id used to scope the INITIAL bootstrap login token (`POST
// /auth/login/:businessId`). The backend treats this as a hint only — it
// re-resolves to a contract the user actually belongs to (preferring one with
// ADMIN_PORTAL) — so we pass the user's last-used project when known, else the
// default. Replaces the hardcoded `/auth/login/1`.
export const getLoginContractHint = (): string =>
  getLastContractId() ?? DEFAULT_CONTRACT_ID;

export const isSuperAdmin = (user: any): boolean => user?.isSuperAdmin === true;

// Read the refresh token straight from document.cookie. The `react-cookie` hook
// snapshot can be stale/undefined mid-session, and connection.login() decodes the
// refresh token (passing undefined throws "Invalid token specified"). This always
// reflects the current cookie.
export const getRefreshTokenCookie = (): string | undefined => {
  const m =
    typeof document !== 'undefined'
      ? document.cookie.match(/(?:^|;\s*)refreshToken=([^;]+)/)
      : null;
  return m ? decodeURIComponent(m[1]) : undefined;
};

// Decode a JWT payload (no verification — just to read claims like isSuperAdmin /
// businessId right after login, before the useUser() hook re-reads the cookie).
export const decodeJwt = (token?: string): any => {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    return JSON.parse(
      decodeURIComponent(
        atob(part.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      ),
    );
  } catch {
    return null;
  }
};

// Visual constants for the contract UI (module badge labels). Display only — the
// stored moduleKey ('bin-bag-management') is unchanged, so existing projects are
// unaffected; we just relabel it in the UI.
export const MODULE_LABELS: Record<string, string> = {
  'bin-bag-management': 'Store Collection',
  'bin-management': 'Bin Management',
};

// Display-only rename of project-type names. The STORED value is unchanged (so
// existing projects, links and data are unaffected — projects reference the type
// by id, not by this name); we only relabel it in the UI.
const CONTRACT_TYPE_NAME_OVERRIDES: Record<string, string> = {
  'Bin Bag Collection': 'Store Collection',
};
export const displayContractTypeName = (name?: string): string =>
  (name && CONTRACT_TYPE_NAME_OVERRIDES[name]) || name || '';

// Modules that are NOT built yet (future development). A project on one of these
// can't be opened in the portal — the Open action is disabled until the module
// ships. A project with no module set is treated as available.
export const UNAVAILABLE_MODULE_KEYS = new Set<string>(['bin-management']);
export const isModuleAvailable = (moduleKey?: string): boolean =>
  !moduleKey || !UNAVAILABLE_MODULE_KEYS.has(moduleKey);

// Fetch the contracts the authenticated user may access (super-admin = all).
export const useContracts = () => {
  const connection = useConnection();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    connection
      // Web only: list projects the user can actually enter (ADMIN_PORTAL).
      // A driver-only membership won't appear here.
      .get('auth/contracts', { params: { platform: 'web' } })
      .then((res) => {
        if (active) setContracts(res.data ?? []);
      })
      .catch(() => {
        if (active) setContracts([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [connection]);

  return { contracts, loading };
};

// Route guard for the super-admin shell. Renders children only for a platform
// super-admin; everyone else is bounced to their operational portal / login.
export const SuperAdminGuard: FC<PropsWithChildren> = ({ children }) => {
  const user = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === null) {
      navigate('/login');
    } else if (!isSuperAdmin(user)) {
      const last = getLastContractId();
      navigate(last ? `/portal/${last}/dashboard` : '/login');
    }
  }, [user, navigate]);

  if (!user || !isSuperAdmin(user)) {
    return (
      <div className="flex items-center justify-center w-full h-screen">
        <Spin size="large" />
      </div>
    );
  }
  return <>{children}</>;
};
